/**
 * The parts of a model-call viewer shared by the AI Review prompt viewer
 * (`PromptViewerModal`) and the Company Map one
 * (`features/company-map-prompt/CompanyMapPromptModal`): the kicker, a
 * preformatted block, "Copy as JSON", the attempts table and a sidebar row.
 * Moved out of `PromptViewerModal.tsx` unchanged.
 */
import { Button } from "@nu-assist/ui";
import { Check, Copy } from "lucide-react";
import { type ReactNode, useState } from "react";

import type { AiReviewContactCall } from "../../clients/aiReview.client";
import { fmtSeconds } from "../person-panel/format";

export function Kicker({ children }: { children: ReactNode }) {
  return (
    <span className="text-micro font-semibold uppercase tracking-wide text-accent-ink">
      {children}
    </span>
  );
}

export function Pre({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <pre
      data-testid={testId}
      className="whitespace-pre-wrap break-words rounded-md border border-border bg-surface-raised px-3 py-2 font-mono text-caption text-text"
    >
      {children}
    </pre>
  );
}

export function CopyJsonButton({ text }: { text: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  async function copy() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch {
      setState("failed");
    }
  }
  return (
    <Button variant="secondary" type="button" onClick={copy} data-testid="prompt-copy-json">
      {state === "copied" ? (
        <Check className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Copy className="h-4 w-4" aria-hidden="true" />
      )}
      {state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : "Copy as JSON"}
    </Button>
  );
}

/** Reads only `attempts`, so any recorded call with the same hop shape fits
 *  (the AI Review call, the Company Map one). */
export function AttemptsPanel({
  call,
}: { call: Pick<AiReviewContactCall, "attempts"> | undefined }) {
  const attempts = call?.attempts ?? [];
  if (attempts.length === 0) {
    return (
      <p className="text-body text-text-secondary" data-testid="prompt-attempts-empty">
        No attempts were recorded for this call.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-caption" data-testid="prompt-attempts">
        <thead className="text-text-muted">
          <tr>
            <th className="py-1 pr-3 font-medium">#</th>
            <th className="py-1 pr-3 font-medium">Route</th>
            <th className="py-1 pr-3 font-medium">Latency</th>
            <th className="py-1 pr-3 font-medium">Request id</th>
            <th className="py-1 font-medium">Error</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border text-text">
          {attempts.map((a, i) => (
            <tr key={`${i}-${a.request_id ?? a.route}`} data-testid="prompt-attempt">
              <td className="py-1 pr-3 tabular-nums">{i + 1}</td>
              <td className="break-all py-1 pr-3 font-mono">{a.route}</td>
              <td className="py-1 pr-3 tabular-nums">
                {a.latency_ms != null ? fmtSeconds(a.latency_ms) : "—"}
              </td>
              <td className="break-all py-1 pr-3 font-mono">{a.request_id ?? "—"}</td>
              <td className="py-1">
                {a.error_code || a.error_kind ? (
                  <span className="text-error-ink">
                    {[a.error_code, a.error_kind].filter(Boolean).join(" · ")}
                  </span>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SideRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-micro uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="break-words text-caption text-text">{children}</dd>
    </div>
  );
}
